import { RefObject, useCallback, useLayoutEffect, useState } from 'react';

type UseDropdownPlacementOptions = {
  containerRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  gap?: number;
  maxHeight: number;
  minHeight: number;
  preferredHeight: number;
  viewportGap: number;
};

type FixedPosition = {
  top: number;
  left: number;
  width: number;
};

type DropdownPlacementState = {
  isOpenAbove: boolean;
  maxHeight: number;
  fixedPosition?: FixedPosition;
};

const DROPDOWN_GAP = 8;

const useDropdownPlacement = ({
  containerRef,
  isOpen,
  gap = DROPDOWN_GAP,
  maxHeight: maxHeightOption,
  minHeight,
  preferredHeight,
  viewportGap,
}: UseDropdownPlacementOptions): DropdownPlacementState => {
  const [state, setState] = useState<DropdownPlacementState>({
    isOpenAbove: false,
    maxHeight: maxHeightOption,
  });

  const updatePlacement = useCallback(() => {
    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const boundaryBottom = viewportHeight - viewportGap;
    const boundaryTop = viewportGap;

    const availableBelow = boundaryBottom - containerRect.bottom;
    const availableAbove = containerRect.top - boundaryTop;
    const isOpenAbove = availableBelow < preferredHeight && availableAbove > availableBelow;
    const available = isOpenAbove ? availableAbove : availableBelow;
    const maxHeight = Math.min(maxHeightOption, Math.max(minHeight, available));

    const fixedPosition: FixedPosition = {
      top: isOpenAbove ? containerRect.top - gap - maxHeight : containerRect.bottom + gap,
      left: containerRect.left,
      width: containerRect.width,
    };

    setState({ isOpenAbove, maxHeight, fixedPosition });
  }, [containerRef, gap, viewportGap, preferredHeight, maxHeightOption, minHeight]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updatePlacement();

    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen, updatePlacement]);

  return state;
};

export default useDropdownPlacement;
